import { getEgsMarginPercent } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/egs/egsUtils';

import { convertAmountToAud } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';

import { EGS_BEDDING_LABELS, EGS_CATS } from './fields';
import type {
  EgsApplyMode,
  EgsCategoryMeta,
  EgsDraft,
  EgsDraftInput,
  EgsGuestCategoryDraft,
  EgsGuestCategoryKey,
  EgsGuestType,
  EgsPackageOption,
  EgsRoomDraft,
} from './types';

export function getEgsRoomLabel(draft: EgsDraft) {
  if (draft.isAdditionalNightlyCost && draft.displayRoomName) {
    return draft.displayRoomName;
  }
  if (draft.rooms === 'all') {
    return 'All rooms';
  }
  return draft.rooms.join(', ');
}

export function getEgsBeddingLabel(draft: EgsDraft) {
  if (draft.beddingType === 'other' && draft.otherBeddingType) {
    return draft.otherBeddingType;
  }
  return draft.beddingType ? EGS_BEDDING_LABELS[draft.beddingType] : '';
}

export function defaultEgsCategoryDraft(): EgsGuestCategoryDraft {
  return {
    enabled: false,
    fromAge: '',
    toAge: '',
    inclusions: '',
    beddingType: '',
    otherBeddingType: '',
    rateType: '',
    costPrice: '',
    sellPriceAud: '',
    applyMode: 'this',
    applyRooms: [],
    upgradeNightlyCost: '',
    upgradePackageOptions: [],
  };
}

export function guestCategoryKeyForType(guestType: EgsGuestType): EgsGuestCategoryKey {
  return EGS_CATS.find((meta) => meta.guestType === guestType)?.key ?? 'adult';
}

// Upgrade fields are left blank - no link exists from a record back to the sibling
// "Additional Nightly Cost" row (if any) it produced, so editing can't repopulate them.
export function categoryDraftFromRecord(record: EgsDraft): EgsGuestCategoryDraft {
  return {
    enabled: true,
    fromAge: String(record.fromAge),
    toAge: String(record.toAge),
    inclusions: record.inclusions,
    beddingType: record.beddingType,
    otherBeddingType: record.otherBeddingType,
    rateType: record.rateType,
    costPrice: String(record.costPrice),
    sellPriceAud: String(record.sellPriceAud),
    applyMode: record.rooms === 'all' ? 'all' : record.rooms.length > 1 ? 'particular' : 'this',
    applyRooms: record.rooms === 'all' ? [] : record.rooms,
    upgradeNightlyCost: '',
    upgradePackageOptions: [],
  };
}

export function defaultEgsRoomDraft(): EgsRoomDraft {
  return {
    globalApplyMode: 'this',
    globalApplyRooms: [],
    adult: defaultEgsCategoryDraft(),
    child: defaultEgsCategoryDraft(),
    infant: defaultEgsCategoryDraft(),
  };
}

export function buildEgsRecord(
  category: EgsGuestCategoryDraft,
  meta: EgsCategoryMeta,
  rooms: 'all' | Array<string>,
  currency: string,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): EgsDraftInput {
  const fromAge = meta.ages === 'to' ? 0 : Number(category.fromAge);
  const toAge = meta.ages === 'from' ? 99 : Number(category.toAge);
  const costPrice = Number(category.costPrice);
  const sellPriceAud = Number(category.sellPriceAud);
  // Callers gate on a resolved, positive rate before reaching here (see applyRoomToPending),
  // so the costPrice fallback below is defensive only and should be unreachable.
  const convertedCost = convertAmountToAud(costPrice, currency, audToSourceCurrencyRate) ?? costPrice;

  return {
    rooms,
    guestType: meta.guestType,
    fromAge,
    toAge,
    collectedBy: costPrice === 0 && sellPriceAud === 0 ? 'free_of_charge' : 'paid_at_booking',
    beddingType: category.beddingType,
    otherBeddingType: category.otherBeddingType,
    rateType: category.rateType,
    inclusions: category.inclusions.trim(),
    currency,
    costPrice,
    convertedCost,
    sellPriceAud,
    marginPct: getEgsMarginPercent(convertedCost, sellPriceAud),
  };
}

export function buildEgsUpgradeRecord(
  category: EgsGuestCategoryDraft,
  meta: EgsCategoryMeta,
  roomName: string,
  currency: string,
  availablePackageOptions: Array<EgsPackageOption>,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): EgsDraftInput | null {
  const upgradeAmount = Number(category.upgradeNightlyCost);

  if (Number.isNaN(upgradeAmount) || upgradeAmount <= 0 || !category.upgradePackageOptions.length) {
    return null;
  }

  const byId = new Map(availablePackageOptions.map((option) => [option.id, option]));
  const packageOptions = category.upgradePackageOptions.map((id) => byId.get(id) ?? { id, label: id });

  const record = buildEgsRecord(
    { ...category, costPrice: String(upgradeAmount) },
    meta,
    [roomName],
    currency,
    audToSourceCurrencyRate,
  );

  return {
    ...record,
    isAdditionalNightlyCost: true,
    displayRoomName: `Additional Nightly Cost ${roomName}`,
    packageOptions,
  };
}

export const EGS_APPLY_MODE_LABELS: Record<EgsApplyMode, string> = {
  this: 'This room only',
  all: 'All rooms',
  particular: 'Select rooms',
};
